import { queryOptions } from '@tanstack/react-query';
import type {
  AppointmentsSummary,
  Desk,
  GlobalAppointment,
  RecentTicketEvent,
  TicketListItem,
  TicketsSummary,
} from '@erp/shared';
import { api, apiList } from '@/lib/api-client';
import { getStoredUser } from '@/auth/use-auth';

/** Counts via X-Total-Items with limit=1 — never transfers the actual rows. */
async function countTickets(filter: {
  status: 'open' | 'closed' | 'canceled';
  desk_ids?: number[];
  responsible_ids?: number[];
}): Promise<number> {
  const res = await apiList<TicketListItem>('/tickets', {
    query: {
      status: filter.status,
      desk_ids: filter.desk_ids,
      responsible_ids: filter.responsible_ids,
      limit: 1,
    },
  });
  if (res.error) throw res.error;
  return res.data.total;
}

export function desksQueryOptions() {
  return queryOptions({
    queryKey: ['erp', 'dashboard', 'desks'],
    queryFn: async () => {
      const res = await apiList<Desk>('/desks', { query: { limit: 200 } });
      if (res.error) throw res.error;
      return res.data.items;
    },
  });
}

/**
 * KPIs do "Resumo de tickets". Usa GET /tickets/summary quando o endpoint
 * existe; senão degrada para contagens por status (campos sem dado = null).
 */
export interface DashboardSummary {
  open: number;
  mine: number | null;
  unassigned: number | null;
  runningTimers: number | null;
  closedToday: number | null;
}

export function ticketsSummaryQueryOptions() {
  return queryOptions({
    queryKey: ['erp', 'dashboard', 'tickets-summary'],
    queryFn: async (): Promise<DashboardSummary> => {
      const res = await api<TicketsSummary>('/tickets/summary');
      if (!res.error) return res.data;
      // Fallback: endpoint ainda não disponível → conta o que dá via lista.
      const me = getStoredUser();
      const [open, mine] = await Promise.all([
        countTickets({ status: 'open' }),
        me ? countTickets({ status: 'open', responsible_ids: [me.id] }) : Promise.resolve(null),
      ]);
      return { open, mine, unassigned: null, runningTimers: null, closedToday: null };
    },
  });
}

// ── Minhas horas apontadas (Ontem / Hoje / Semana) ──────────────────────────
function toISODate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

function startOfWeek(d: Date): Date {
  const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = copy.getDay(); // 0 = domingo
  const diff = day === 0 ? 6 : day - 1; // semana começa na segunda
  copy.setDate(copy.getDate() - diff);
  return copy;
}

function appointmentMinutes(a: GlobalAppointment, now: number): number {
  const end = a.endedAt !== null ? new Date(a.endedAt).getTime() : now;
  return Math.max(0, Math.round((end - new Date(a.startedAt).getTime()) / 60_000));
}

export function myHoursQueryOptions() {
  return queryOptions({
    queryKey: ['erp', 'dashboard', 'my-hours'],
    queryFn: async (): Promise<AppointmentsSummary> => {
      const res = await api<AppointmentsSummary>('/appointments/summary');
      if (!res.error) return res.data;
      // Fallback: computa localmente a partir do range de apontamentos.
      const me = getStoredUser();
      if (!me) return { todayMinutes: 0, yesterdayMinutes: 0, weekMinutes: 0 };
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const weekStart = startOfWeek(today);
      const rangeStart = yesterday < weekStart ? yesterday : weekStart;

      const list = await apiList<GlobalAppointment>('/appointments', {
        query: {
          start_date: toISODate(rangeStart),
          end_date: toISODate(today),
          user_ids: [me.id],
          limit: 500,
        },
      });
      if (list.error) throw list.error;

      const nowMs = now.getTime();
      let todayMinutes = 0;
      let yesterdayMinutes = 0;
      let weekMinutes = 0;
      const todayKey = toISODate(today);
      const yesterdayKey = toISODate(yesterday);
      for (const a of list.data.items) {
        const started = new Date(a.startedAt);
        const key = toISODate(started);
        const minutes = appointmentMinutes(a, nowMs);
        if (key === todayKey) todayMinutes += minutes;
        if (key === yesterdayKey) yesterdayMinutes += minutes;
        if (started >= weekStart) weekMinutes += minutes;
      }
      return { todayMinutes, yesterdayMinutes, weekMinutes };
    },
  });
}

/** Key do feed global — o realtime (use-realtime-sync) invalida por ela. */
export const recentEventsKey = ['erp', 'dashboard', 'recent-events'] as const;

/** Feed global de eventos. null = endpoint indisponível (o card se esconde). */
export function recentEventsQueryOptions() {
  return queryOptions({
    queryKey: recentEventsKey,
    queryFn: async (): Promise<RecentTicketEvent[] | null> => {
      const res = await api<RecentTicketEvent[]>('/tickets/events/recent', {
        query: { limit: 20 },
      });
      if (res.error) return null;
      return res.data;
    },
  });
}

export interface DeskOpenCount {
  deskId: number;
  count: number;
}

export function deskOpenCountsQueryOptions(deskIds: number[]) {
  return queryOptions({
    queryKey: ['erp', 'dashboard', 'desk-open-counts', deskIds],
    queryFn: (): Promise<DeskOpenCount[]> =>
      Promise.all(
        deskIds.map(async (deskId) => ({
          deskId,
          count: await countTickets({ status: 'open', desk_ids: [deskId] }),
        }))
      ),
    enabled: deskIds.length > 0,
  });
}
